import React, { useEffect } from 'react';
import { useStore } from '../../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faCheck } from '@fortawesome/pro-light-svg-icons';

// services
import ShipSchedulesService from '../../../../../../services/data/ships/shipSchedules';
import PermissionsService from '../../../../../../services/permissions';
import OrderService from '../../../../../../services/orders/order';

// props
import { IShipScheduleResourceShort } from '../../../../../../props/data/ships';

// components
import { DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../../../typography/label';
import Text from '../../../../../typography/text';
import NoAccess from '../../../../../uiframeworks/noAccess';
import DateTimePicker from '../../../../../uiframeworks/forms/dateTimePicker';
import moment from 'moment';

interface IShipScheduleStatusFormProps {
    scheduleId: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    status: string;
    actualDeparture?: string;
    actualArrival?: string;
    estimatedDeparture?: string;
    estimatedArrival?: string;
    notes?: string;
}

const ShipScheduleStatusForm: React.FC<IShipScheduleStatusFormProps> = (props: IShipScheduleStatusFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [schedule, setSchedule] = React.useState<IShipScheduleResourceShort | undefined>(undefined);
    const [data, setData] = React.useState<FormDataProps>({
        status: 'scheduled'
    });

    const hasPermission = PermissionsService.hasPermission(['ships.schedules.update.all', 'orders.trackings.update.all'], user.permissions);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        if (props.scheduleId) {
            const _schedule = await ShipSchedulesService.get(props.scheduleId);
            setSchedule(_schedule);

            let initialStatus = _schedule.status || "scheduled";
            if (initialStatus === 'arrived') initialStatus = 'completed';
            if (initialStatus === 'in progress' || initialStatus === 'in_progress') initialStatus = 'sailing';
            if (initialStatus === 'estimated') initialStatus = 'scheduled';

            setData({
                status: initialStatus,
                actualArrival: _schedule.actualArrival || moment().toISOString(),
                actualDeparture: _schedule.actualDeparture || _schedule.estimatedDeparture || moment().toISOString(),
                estimatedDeparture: _schedule.estimatedDeparture || moment().toISOString(),
                estimatedArrival: _schedule.estimatedArrival || moment().add(3, 'days').toISOString(),
                notes: _schedule.notes || ""
            });
        }

        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (!schedule) {
            return true;
        }
        if (data.status === 'sailing' && !data.actualDeparture) {
            return true;
        }
        if (data.status === 'completed' && !data.actualArrival) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { status, actualArrival, actualDeparture, estimatedArrival, estimatedDeparture, notes } = data;

            let finalActualDeparture = actualDeparture;
            if (status === 'completed' && finalActualDeparture && actualArrival && moment(finalActualDeparture).isAfter(moment(actualArrival))) {
                finalActualDeparture = estimatedDeparture && moment(estimatedDeparture).isSameOrBefore(moment(actualArrival))
                    ? estimatedDeparture
                    : actualArrival;
            }

            // create form data
            let fd = new FormData();
            fd.append("status", status);

            if (status === 'sailing') {
                if (finalActualDeparture) {
                    fd.append('actualDeparture', finalActualDeparture);
                }
                if (estimatedArrival) {
                    fd.append('estimatedArrival', estimatedArrival);
                }
            } else if (status === 'completed') {
                if (finalActualDeparture) {
                    fd.append('actualDeparture', finalActualDeparture);
                }
                if (actualArrival) {
                    fd.append('actualArrival', actualArrival);
                }
            } else if (status === 'scheduled') {
                fd.append('actualDeparture', 'null');
                fd.append('actualArrival', 'null');
                if (estimatedDeparture) {
                    fd.append('estimatedDeparture', estimatedDeparture);
                }
                if (estimatedArrival) {
                    fd.append('estimatedArrival', estimatedArrival);
                }
            }

            if (notes !== undefined) {
                fd.append('notes', notes);
            }

            await ShipSchedulesService.update(props.scheduleId, fd);
            banner.add({
                key: 'update_ship_schedule_status_success',
                variant: 'success',
                icon: faCheck,
                text: `Tracking vessel status updated successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const primaryOrder = schedule?.orders && schedule.orders.length > 0 ? schedule.orders[0] : undefined;

    return <Panel headerText={'Update Tracking Status'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Updating tracking status ..."} /> : null}
            </Stack>;
        }}>
        <Stack className={styles.container} tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && hasPermission ? <>
                    {schedule && (
                        <Stack
                            styles={{
                                root: {
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginBottom: '5px'
                                }
                            }}
                            tokens={{ childrenGap: 6 }}
                        >
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                <span
                                    style={{
                                        background: '#0284c720',
                                        color: '#0284c7',
                                        border: '1px solid #0284c740',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    Shipping (Vessel)
                                </span>
                                {primaryOrder && (
                                    <span
                                        style={{
                                            background: '#e2e8f0',
                                            color: '#475569',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Order #{primaryOrder.orderNumber ? OrderService.getOrderNumber(primaryOrder.orderNumber) : primaryOrder.id?.substring(0, 8)}
                                    </span>
                                )}
                                {schedule.orders && schedule.orders.length > 1 && (
                                    <span
                                        style={{
                                            background: '#f1f5f9',
                                            color: '#64748b',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        +{schedule.orders.length - 1} Orders
                                    </span>
                                )}
                            </Stack>
                            {(schedule.originAddress || schedule.destinationAddress) && (
                                <Text size="small" style={{ color: '#64748b' }}>
                                    {schedule.originAddress || '-'} &rarr; {schedule.destinationAddress || '-'}
                                </Text>
                            )}
                        </Stack>
                    )}

                    <Dropdown
                        label={"Status"}
                        selectedKey={data.status}
                        options={[
                            { key: 'scheduled', text: 'Estimasi' },
                            { key: 'sailing', text: 'Sedang Berjalan' },
                            { key: 'completed', text: 'Selesai' }
                        ]}
                        onChange={(evt, opt) => {
                            data.status = opt ? opt.key as string : 'scheduled';
                            setData({ ...data });
                        }}
                    />

                    {schedule && (
                        <Stack.Item>
                            <Label required>Ship / Vessel</Label>
                            <Stack className={styles.selected} horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign='center'>
                                <Stack>
                                    <Label size={'small'}>{schedule.ship?.name || '-'}</Label>
                                    <Text size={'small'} style={{ color: '#64748b' }}>
                                        {schedule.voy ? `Voy: ${schedule.voy}` : ''}
                                        {schedule.ship?.vendor?.name ? ` • ${schedule.ship.vendor.name}` : ''}
                                        {schedule.ship?.registrationNumber ? ` (${schedule.ship.registrationNumber})` : ''}
                                    </Text>
                                </Stack>
                            </Stack>
                        </Stack.Item>
                    )}

                    {schedule?.ship?.vendor && (
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item styles={{ root: { width: '50%' } }}>
                                <TextField
                                    label={"Shipping Line / Vendor"}
                                    value={schedule.ship.vendor.name || "-"}
                                    disabled={true}
                                />
                            </Stack.Item>
                            <Stack.Item styles={{ root: { width: '50%' } }}>
                                <TextField
                                    label={"Vendor Phone"}
                                    value={schedule.ship.vendor.phoneNumber || "-"}
                                    disabled={true}
                                    prefix={schedule.ship.vendor.phoneNumber ? '+62' : undefined}
                                />
                            </Stack.Item>
                        </Stack>
                    )}

                    {data.status === 'sailing' ? <>
                        <DateTimePicker
                            label={"ACTUAL Departure Date"}
                            required
                            enableTime
                            value={data.actualDeparture}
                            onChange={(date?: string) => {
                                data.actualDeparture = date || data.actualDeparture;
                                setData({ ...data });
                            }}
                            disabled={submitting}
                        />
                        <DateTimePicker
                            label={"ESTIMATED Arrival Date (ETA)"}
                            required
                            enableTime
                            value={data.estimatedArrival}
                            onChange={(date?: string) => {
                                data.estimatedArrival = date || data.estimatedArrival;
                                setData({ ...data });
                            }}
                            disabled={submitting}
                        />
                    </> : null}

                    {data.status === 'completed' ? <>
                        <DateTimePicker
                            label={"ACTUAL Departure Date"}
                            required
                            enableTime
                            value={data.actualDeparture}
                            onChange={(date?: string) => {
                                data.actualDeparture = date || data.actualDeparture;
                                setData({ ...data });
                            }}
                            disabled={submitting}
                        />
                        <DateTimePicker
                            label={"ACTUAL Arrival Date"}
                            required
                            enableTime
                            value={data.actualArrival}
                            onChange={(date?: string) => {
                                data.actualArrival = date || data.actualArrival;
                                setData({ ...data });
                            }}
                            disabled={submitting}
                        />
                    </> : null}

                    <Stack.Item>
                        <TextField
                            label={"Field Notes (Optional)"}
                            multiline
                            rows={3}
                            value={data.notes || ''}
                            disabled={submitting}
                            onChange={(e, val) => {
                                data.notes = val || '';
                                setData({ ...data });
                            }}
                            placeholder="e.g. Kapal antri sandar di dermaga / stuffing selesai jam 17:00..."
                        />
                    </Stack.Item>

                    {schedule?.containers && schedule.containers.length > 0 ? (
                        <Stack.Item>
                            <Label>Related Container{schedule.containers.length > 1 ? ` (${schedule.containers.length})` : ''}</Label>
                            <Stack tokens={{ childrenGap: 6 }}>
                                {schedule.containers.map((c: any, idx: number) => (
                                    <Stack key={c.id || idx} className={styles.selected} horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign='center'>
                                        <Stack>
                                            <Label size={'small'}>{c.containerNumber || 'Container'}</Label>
                                            {c.sealNumber ? <Text size={'small'} style={{ color: '#64748b' }}>Seal: {c.sealNumber}{c.size ? ` • ${c.size}` : ''}</Text> : (c.size ? <Text size={'small'} style={{ color: '#64748b' }}>{c.size}</Text> : null)}
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack.Item>
                    ) : (schedule?.orders && schedule.orders.length > 0 ? (
                        <Stack.Item>
                            <Label>Related Orders ({schedule.orders.length})</Label>
                            <Stack tokens={{ childrenGap: 6 }}>
                                {schedule.orders.map((o: any, idx: number) => (
                                    <Stack key={o.id || idx} className={styles.selected} horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign='center'>
                                        <Stack>
                                            <Label size={'small'}>Order #{o.orderNumber ? OrderService.getOrderNumber(o.orderNumber) : o.id?.substring(0, 8)}</Label>
                                            {o.salesName && <Text size={'small'} style={{ color: '#64748b' }}>Sales: {o.salesName}</Text>}
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack.Item>
                    ) : null)}
                </> : null
            }
            {!hasPermission ? <NoAccess /> : null}
        </Stack>
    </Panel>
};

export default ShipScheduleStatusForm;
